import { Component, OnInit, Optional, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { Ptransaction } from '../../model/ptransaction';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { merge } from 'rxjs';

import { AuthenticationService } from 'src/app/auth/fire-auth/authentication.service';
import { PeriodicTransactionsManager } from '../../core/functions/periodic-transactions-manager.service';

@Component({
  selector: 'makaw-periodic-transaction-creation',
  templateUrl: './periodic-transaction-creation-modal.component.html',
  styleUrls: ['./periodic-transaction-creation-modal.component.scss'],
})
export class PeriodicTransactionCreationModalComponent
  implements OnInit, OnDestroy {
  private destroyed$ = new Subject();
  private entryForm: FormGroup;
  public action: string;
  public headerMessage: string;
  public cancel = false;
  public ptransaction: Ptransaction;
  public today: Date;
  public tomorrow: Date;
  public day = 86400000;
  public times = [
    { label: 'transactions.eachDay', value: 1 },
    { label: 'transactions.eachTwoDays', value: 2 },
    { label: 'transactions.eachWeak', value: 7 },
    { label: 'transactions.each2Weaks', value: 14 },
    { label: 'transactions.eachMonth', value: 30 },
    { label: 'transactions.each2Months', value: 60 },
    { label: 'transactions.each3Months', value: 90 },
    { label: 'transactions.each4Months', value: 120 },
    { label: 'transactions.each6Months', value: 180 },
    { label: 'transactions.annually', value: 365 },
  ];

  constructor(
    public dialogRef: MatDialogRef<PeriodicTransactionCreationModalComponent>,
    // @Optional() is used to prevent error if no data is passed
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private authenticationService: AuthenticationService,
    private periodicTransactionsManager: PeriodicTransactionsManager
  ) {
    this.today = new Date();
    this.tomorrow = new Date(new Date().getTime() + 86400000);
    dialogRef.disableClose = true;
    this.headerMessage = this.data.msg;
    this.action = data.action;
    this.buildCreateForm();
  }

  ngOnInit() {
    const eachControl$ = this.entryForm.get('each').valueChanges;
    const startAtControl$ = this.entryForm.get('startAt').valueChanges;

    merge(eachControl$, startAtControl$)
      .pipe(debounceTime(1000), takeUntil(this.destroyed$))
      .subscribe((value) => this.datesValidator());
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  datesValidator() {
    const c = this.entryForm;
    const startTime = c.get('startAt').value
      ? new Date(c.get('startAt').value)
      : this.today;
    const pattern = c.get('each').value ? c.get('each').value : 1;

    this.tomorrow = this.periodicTransactionsManager.calculateNext(
      pattern,
      startTime
    );
  }

  get f() {
    return this.entryForm.controls;
  }

  public onSubmit() {
    if (!this.cancel) this.doAction();
  }

  doAction() {
    if (this.entryForm.valid) {
      this.manageCreateDataInput();
      this.dialogRef.close({ event: this.action, data: this.ptransaction });
    }
  }

  closeDialog() {
    this.cancel = true;
    this.dialogRef.close({ event: 'Cancel' });
  }

  private manageCreateDataInput() {
    const startAt = new Date(this.entryForm.value.startAt);
    const endAt = new Date(this.entryForm.value.endAt);
    const amount = Math.abs(this.entryForm.value.amount);

    this.ptransaction = {
      startAt: startAt.getTime(),
      each: this.entryForm.value.each,
      endAt: endAt.getTime(),
      amount: this.entryForm.value.type === 'Expense' ? amount * -1 : amount,
      id: this.data.id ? this.data.id : new Date().getTime(),
      owner: this.authenticationService.currentUserId,
      description: this.entryForm.value.description,
      category: this.entryForm.value.category,
      updated: 0,
    };

    this.action = this.data.action;
  }

  private manageEditDataInput() {
    const amount = Math.abs(this.entryForm.value.amount);

    this.ptransaction = {
      startAt: this.data.startAt,
      each: this.data.each,
      endAt: this.data.endAt,
      amount: this.entryForm.value.type === 'Expense' ? amount * -1 : amount,
      id: this.data.id ? this.data.id : new Date().getTime(),
      owner: this.authenticationService.currentUserId,
      description: this.entryForm.value.description,
      category: this.entryForm.value.category,
      updated: this.data.updated ? this.data.updated : 0,
    };

    this.action = this.data.action;
  }

  buildCreateForm() {
    this.entryForm = this.formBuilder.group({
      type: new FormControl(
        this.data.amount ? (this.data.amount < 0 ? 'Expense' : 'Income') : '',
        Validators.compose([Validators.required, Validators.maxLength(32)])
      ),
      description: new FormControl(
        this.data.description ? this.data.description : '',
        Validators.compose([Validators.required, Validators.maxLength(32)])
      ),
      category: new FormControl(
        this.data.category ? this.data.category : '',
        Validators.compose([Validators.required, Validators.maxLength(32)])
      ),
      amount: new FormControl(
        this.data.amount ? this.data.amount : '',
        Validators.compose([Validators.required, Validators.maxLength(32)])
      ),
      startAt: new FormControl(
        this.data.startAt ? new Date(this.data.startAt) : '',
        Validators.compose([Validators.required, Validators.maxLength(32)])
      ),
      each: new FormControl(
        this.data.each ? this.data.each : '',
        Validators.compose([Validators.required, Validators.maxLength(32)])
      ),
      endAt: new FormControl(
        this.data.endAt ? new Date(this.data.endAt) : '',
        Validators.compose([Validators.required, Validators.maxLength(32)])
      ),
    });
  }

  buildEditForm() {
    this.entryForm = this.formBuilder.group({
      type: new FormControl(
        this.data.amount ? (this.data.amount < 0 ? 'Expense' : 'Income') : '',
        Validators.compose([Validators.required, Validators.maxLength(32)])
      ),
      description: new FormControl(
        this.data.description ? this.data.description : '',
        Validators.compose([Validators.required, Validators.maxLength(32)])
      ),
      category: new FormControl(
        this.data.category ? this.data.category : '',
        Validators.compose([Validators.required, Validators.maxLength(32)])
      ),
      amount: new FormControl(
        this.data.amount ? this.data.amount : '',
        Validators.compose([Validators.required, Validators.maxLength(32)])
      ),
    });
  }
}
